import React, {Suspense, useContext, useEffect} from 'react';
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom';
import {isMobile} from 'react-device-detect';
import {DesktopLayout} from "../layout/DesktopLayout";
import Home from "../../pages/Home";
import Users from "../../pages/Users";
import RegisterUser from "../../pages/RegisterUser";
import {MobileLayout} from "../layout/MobileLayout";
import Patients from "../../pages/Patients";
import PatientDetails from "../../pages/PatientDetails";
import PatientCreate from "../../pages/PatientCreate";
import PatientImport from "../../pages/PatientImport";
import ImportHistory from "../../pages/ImportHistory";
import Components from "../../pages/Components";
import MedicalStaff from "../../pages/MedicalStaff";
import MedicalStaffCreate from "../../pages/MedicalStaffCreate";
import MedicalStaffDetails from "../../pages/MedicalStaffDetails";
import Organizations from "../../pages/Organizations";
import OrganizationCreate from "../../pages/OrganizationCreate";
import OrganizationDetails from '../../pages/OrganizationDetails';
import CalendarPage from "../../pages/CalendarPage";
import Settings from "../../pages/Settings";
import ActivityLog from "../../pages/ActivityLog";
import PatientSelfRegister from '../../pages/PatientSelfRegister';

import Organization from "../../pages/Organization";
import Clinics from "../organization/Clinics";
import Nzis from "../../pages/Nzis";
import axiosRetry from "axios-retry";
import axios from "axios";
import Reports from "../../pages/Reports";
import ChoosePlan from "../../pages/ChoosePlan";
import Registration from "../../pages/Registration";
import Login from "../../pages/Login";
import ForgotPassword from "../../pages/ForgotPassword";
import ResetPassword from "../../pages/ResetPassword";
import AcceptInvitePage from "../../pages/AcceptInvitePage";
import VerifyEmailPage from "../../pages/VerifyEmailPage";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import BillingPage from "../../pages/BillingPage";
import EmailQueue from "../../pages/EmailQueue";
import {Helmet} from "react-helmet-async";
import GoogleCalendarCallback from "../../pages/GoogleCalendarCallback";
import HelpPage from "../../pages/HelpPage";
import HelpArticlePage from "../../pages/HelpArticlePage";
import ProfilePage from "../../pages/ProfilePage";
import RouteTracker from './RouteTracker';

export const AppRouter = () => {

    const {selectSubscription} = useContext(DentobookApplicationContext);

    useEffect(() => {
        axiosRetry(axios, {
            retries: 1,
            retryDelay: () => 1000,
            retryCondition: (error) => {
                // Network failure (no response) — safe to retry any method.
                if (!error.response) return true;

                // Only retry idempotent methods on 5xx server errors.
                // Never retry POST/PUT/PATCH/DELETE — replaying mutations
                // causes duplicate side effects (e.g. double Turnstile token
                // consumption, duplicate resource creation).
                const method = error.config?.method?.toLowerCase();
                if (['get', 'head', 'options'].includes(method!) && error.response.status >= 500) {
                    return true;
                }

                return false;
            },
        });
    }, []);

    if (selectSubscription) {
        return <BrowserRouter>
            <RouteTracker />
            <Suspense fallback="Loading...">
                <Routes>
                    <Route path="*" element={<ChoosePlan/>}/>
                </Routes>
            </Suspense>
        </BrowserRouter>;
    }

    return (
    <BrowserRouter>
        <RouteTracker />
        <Suspense fallback="Loading...">
            <Helmet>
                <title>Dentobook Dental Management Software</title>
                <meta name="description" content="Smarter tools, better practice."/>
            </Helmet>
            <Routes>
                <Route key="login" path="/login" element={<Login/>}/>
                <Route key="registration" path="/register" element={<Registration/>}/>
                <Route key="forgotPassword" path="/forgot-password" element={<ForgotPassword/>}/>
                <Route key="resetPassword" path="/reset-password" element={<ResetPassword/>}/>
                <Route key="googleCalendarCallback" path="/google-calendar-callback" element={<GoogleCalendarCallback/>}/>
                <Route key="acceptInvite" path="/accept-invite" element={<AcceptInvitePage/>}/>
                <Route key="verifyEmail" path="/verify-email" element={<VerifyEmailPage/>}/>
                <Route element={isMobile ? <MobileLayout/> : <DesktopLayout/>}>
                    <Route key="home" path="/" element={<Home/>}/>
                    <Route key="users" path="/users" element={<Users/>}/>
                    <Route key="register-new-user" path="/register-new-user" element={<RegisterUser/>}/>
                    <Route key="components" path="/components" element={<Components/>}/>
                    <Route key="patients" path="/patients" element={<Patients/>}/>
                    <Route key="patient" path="/patients/:id" element={<PatientDetails/>}/>
                    <Route key="patientsCreate" path="/patients/create" element={<PatientCreate/>}/>
                    <Route key="patientsImport" path="/patients/import" element={<PatientImport/>}/>
                    <Route key="importHistory" path="/patients/import/history" element={<ImportHistory/>}/>
                    <Route key="patientsCreateAnonymous" path="/patients/self-register/:id/"
                           element={<PatientSelfRegister/>}/>
                    <Route key="medicalStaff" path="/medical/staff" element={<MedicalStaff/>}/>
                    <Route key="medicalStaffCreate" path="/medical/staff/create"
                           element={<MedicalStaffCreate/>}/>
                    <Route key="medicalStaffDetails" path="/medical/staff/:id"
                           element={<MedicalStaffDetails/>}/>
                    <Route key="organizations" path="/organizations" element={<Organizations/>}/>
                    <Route key="organizationCreate" path="/organizations/create"
                           element={<OrganizationCreate/>}/>
                    <Route key="organizationDetails" path="/organizations/:id"
                           element={<OrganizationDetails/>}/>
                    <Route key="emailQueue" path="/email-queue" element={<EmailQueue/>}/>
                    <Route key="calendar" path="/calendar" element={<CalendarPage/>}/>
                    <Route key="settings" path="/settings" element={<Settings/>}/>
                    <Route key="activityLog" path="/activity-log" element={<ActivityLog/>}/>
                    <Route key="organization_settings" path="/organization" element={<Organization/>}/>
                    <Route key="clinics" path="/clinics" element={<Clinics/>}/>
                    <Route key="nzis" path="/nzis" element={<Nzis/>}/>
                    <Route key="reports" path="/reports" element={<Reports/>}/>
                    <Route key="billing" path="/billing" element={<BillingPage/>}/>
                    <Route key="help" path="/help" element={<HelpPage/>}/>
                    <Route key="helpArticle" path="/help/:slug" element={<HelpArticlePage/>}/>
                    <Route key="profile" path="/profile" element={<ProfilePage/>}/>
                </Route>
            </Routes>
        </Suspense>
    </BrowserRouter>
)
    ;
};
